import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, ArrowRight, LogIn } from 'lucide-react';
import logo from '../assets/logo.png';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  // Redirigir si ya está autenticado (solo una vez al cargar)
  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData);

    if (result.success) {
      toast.success('¡Bienvenido de vuelta!');
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2d3c5d] via-[#3d4c6d] to-[#2d3c5d] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#eddacb] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#eddacb] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-[#eddacb] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
         
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            ¡Hola de nuevo!
          </h1>
          <p className="text-[#eddacb] text-lg">Iniciá sesión en tu cuenta</p>
        </div>

        {/* Formulario */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 animate-fade-in animation-delay-200">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div className="group">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#2d3c5d]" />
                Correo electrónico
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3.5 pl-11 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all duration-300 group-hover:border-gray-300"
                  placeholder="tu@email.com"
                />
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Password Input */}
            <div className="group">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#2d3c5d]" />
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3.5 pl-11 pr-11 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] focus:border-transparent transition-all duration-300 group-hover:border-gray-300"
                  placeholder="Tu contraseña"
                />
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2d3c5d] transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-[#2d3c5d] border-gray-300 rounded focus:ring-[#2d3c5d] cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">Recordarme</span>
              </label>
              <Link
                to="/recuperar-password"
                className="text-sm text-[#2d3c5d] hover:text-[#1f2a3d] font-medium hover:underline transition-all"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#2d3c5d] to-[#3d4c6d] text-white py-3.5 rounded-xl font-semibold
                       hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]
                       transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                       flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Iniciando sesión...
                </>
              ) : (
                <>
                  Iniciar sesión
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              ¿No tenés cuenta?{' '}
              <Link
                to="/register"
                className="text-[#2d3c5d] font-bold hover:text-[#1f2a3d] hover:underline transition-all"
              >
                Registrate gratis
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t-2 border-gray-100">
            <Link
              to="/"
              className="block w-full py-3 rounded-xl font-semibold text-center border-2 border-[#2d3c5d] text-[#2d3c5d]
                       hover:bg-[#2d3c5d] hover:text-white transition-all duration-300
                       hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
            >
              Volver al inicio
            </Link>
          </div>
        </div>

        {/* Extra info */}
        <p className="text-center text-white/70 text-sm mt-6">
          Al iniciar sesión aceptás nuestros{' '}
          <Link to="/terminos-condiciones" className="underline hover:text-white transition-colors">
            Términos y Condiciones
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
